import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack,
  IconButton,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpensesInput from "./ExpensesInput"
import i18n from "i18n"

const ExpensesPartidaItem = ({
  partida,
  selectedItems,
  allocation,
  setAllocation,
  onChildEdit = () => {},
  expandedAccordions = {},
  setExpandedAccordions = () => {},
}) => {
  const isPartidaSelected = !!selectedItems[partida.partidaId]
  const selectedDecomps = Array.isArray(partida.decomposition)
    ? partida.decomposition
        .map((item, idx) => ({ ...item, idx }))
        .filter(item => selectedItems[`${partida.partidaId}_${item.idx}`])
    : []
  if (!isPartidaSelected && selectedDecomps.length === 0) return null

  const lastChildEdit = useRef(0)
  const lastPartidaEdit = useRef(0)
  const markChildEditLocal = () => {
    lastChildEdit.current = Date.now()
  }
  const markPartidaEdit = () => {
    lastPartidaEdit.current = Date.now()
  }

  useEffect(() => {
    setAllocation(prev => {
      if (
        prev[partida.partidaId] &&
        Array.isArray(prev[partida.partidaId].decomposition)
      )
        return prev
      const decompLength = Array.isArray(partida.decomposition)
        ? partida.decomposition.length
        : 0
      return {
        ...prev,
        [partida.partidaId]: {
          ...prev[partida.partidaId],
          projectId: partida.projectId || prev[partida.partidaId]?.projectId,
          allocated: prev[partida.partidaId]?.allocated || 0,
          decomposition: Array.from(
            { length: decompLength },
            (_, i) => prev[partida.partidaId]?.decomposition?.[i] ?? 0,
          ),
        },
      }
    })
  }, [partida.partidaId, partida.decomposition, setAllocation])

  const value = allocation?.[partida.partidaId]?.allocated || 0

  const distributeValueIntoParts = (total, n) => {
    if (n <= 0) return []
    const totalCents = Math.round((Number(total) || 0) * 100)
    const base = Math.floor(totalCents / n)
    const remainder = totalCents % n
    return Array.from({ length: n }, (_, i) => {
      const extra = remainder > 0 && i >= n - remainder ? 1 : 0
      return (base + extra) / 100
    })
  }

  const handleInputChange = values => {
    const newValue = values.floatValue || 0
    const decompLength = Array.isArray(partida.decomposition)
      ? partida.decomposition.length
      : 0
    const partidaExpanded = expandedAccordions[partida.partidaId] ?? false

    const sumDecomps = Array.isArray(
      allocation?.[partida.partidaId]?.decomposition,
    )
      ? allocation[partida.partidaId].decomposition.reduce(
          (acc, v) => acc + (v || 0),
          0,
        )
      : 0

    if (Math.abs(newValue - sumDecomps) < 0.0005) {
      setAllocation(prev => ({
        ...prev,
        [partida.partidaId]: {
          ...(prev[partida.partidaId] || {}),
          allocated: newValue,
        },
      }))
      markPartidaEdit()
      return
    }

    if (
      lastChildEdit.current > (lastPartidaEdit.current || 0) &&
      Date.now() - lastChildEdit.current < 50
    ) {
      setAllocation(prev => ({
        ...prev,
        [partida.partidaId]: {
          ...(prev[partida.partidaId] || {}),
          allocated: newValue,
        },
      }))
      markPartidaEdit()
      return
    }

    setAllocation(prev => {
      const existing = prev[partida.partidaId] || {}

      if (decompLength > 0 && partidaExpanded) {
        const selectedDecompIndices = partida.decomposition
          .map((d, i) => i)
          .filter(i => !!selectedItems[`${partida.partidaId}_${i}`])

        if (selectedDecompIndices.length > 0) {
          const distributed = distributeValueIntoParts(
            newValue,
            selectedDecompIndices.length,
          )
          const newDecomp = Array.from({ length: decompLength }, (_, i) => {
            const selIdx = selectedDecompIndices.indexOf(i)
            return selIdx >= 0
              ? distributed[selIdx]
              : (existing.decomposition?.[i] ?? 0)
          })
          const next = {
            ...prev,
            [partida.partidaId]: {
              ...existing,
              projectId: partida.projectId || existing.projectId,
              allocated: newDecomp.reduce((a, b) => a + (b || 0), 0),
              decomposition: newDecomp,
            },
          }
          lastPartidaEdit.current = Date.now()
          return next
        }
        const nextClosed = {
          ...prev,
          [partida.partidaId]: {
            ...existing,
            projectId: partida.projectId || existing.projectId,
            allocated: newValue,
            decomposition: Array.from({ length: decompLength }, () => 0),
          },
        }
        lastPartidaEdit.current = Date.now()
        return nextClosed
      }

      return {
        ...prev,
        [partida.partidaId]: {
          ...existing,
          projectId: partida.projectId || existing.projectId,
          allocated: newValue,
          decomposition:
            decompLength > 0
              ? Array.from({ length: decompLength }, () => 0)
              : existing.decomposition || [],
        },
      }
    })

    try {
      markPartidaEdit()
    } catch (e) {}
    try {
      onChildEdit()
    } catch (e) {}
  }

  if (
    !Array.isArray(partida.decomposition) ||
    partida.decomposition.length === 0
  ) {
    return (
      <Box
        sx={{
          ml: 2,
          my: 0.5,
          height: "38px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            ml: 3,
            fontWeight: "medium",
            fontSize: "0.9rem",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {partida.name}
        </Typography>
        <Box sx={{ ml: 2, minWidth: 70 }}>
          <ExpensesInput
            value={value}
            onChange={handleInputChange}
            size="small"
          />
        </Box>
      </Box>
    )
  }

  // Si hay descomposiciones seleccionadas mostrar inputs para cada una, pero sin redistribuir.
  const partidaExpanded = expandedAccordions[partida.partidaId] ?? false
  const handlePartidaToggle = (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [partida.partidaId]: isExpanded,
    }))
  }

  return (
    <Accordion
      sx={{
        ml: 2,
        my: 0.5,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
      expanded={partidaExpanded}
    >
      <AccordionSummary
        // prevent toggling when clicking the summary area
        onClick={e => e.stopPropagation()}
        expandIcon={
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              handlePartidaToggle(null, !partidaExpanded)
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
        aria-controls={`expenses-partida-panel-${partida.partidaId}-content`}
        id={`expenses-partida-panel-${partida.partidaId}-header`}
        sx={{
          minHeight: "38px !important",
          height: "38px",
          paddingInline: 1,
          flexDirection: "row-reverse",
          "&.Mui-expanded": {
            minHeight: "38px !important",
            height: "38px",
            marginBottom: "2px",
          },
          "& .MuiAccordionSummary-content": {
            margin: "6px 0 !important",
            minWidth: 0,
            flex: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "medium",
              fontSize: "0.9rem",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {partida.name}
          </Typography>
          <Box sx={{ ml: 2, minWidth: 70 }}>
            <ExpensesInput
              value={value}
              onChange={handleInputChange}
              size="small"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
        <Stack
          direction="column"
          spacing={1}
          flexWrap="wrap"
          sx={{ ml: "10px" }}
        >
          {selectedDecomps.map(item => {
            const decompValue =
              allocation?.[partida.partidaId]?.decomposition?.[item.idx] || 0
            const handleDecompChange = values => {
              const newVal = values.floatValue || 0
              setAllocation(prev => {
                const length = partida.decomposition?.length || 0
                const arr = Array.from({ length }, (_, i) =>
                  i === item.idx
                    ? newVal
                    : (prev[partida.partidaId]?.decomposition?.[i] ?? 0),
                )
                const sum = arr.reduce((acc, v) => acc + (v || 0), 0)
                return {
                  ...prev,
                  [partida.partidaId]: {
                    ...prev[partida.partidaId],
                    projectId:
                      partida.projectId || prev[partida.partidaId]?.projectId,
                    decomposition: arr,
                    allocated: sum,
                  },
                }
              })

              try {
                markChildEditLocal()
              } catch (e) {}
              try {
                onChildEdit()
              } catch (e) {}
            }
            return (
              <Box
                key={item.idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderLeft: "1px solid",
                  borderColor: "#1c76d2",
                  ml: "7px !important",
                  px: 1,
                  py: 0.5,
                  fontSize: "0.8rem",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.8rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  {item.description ||
                    `${i18n.t("costs.modal.element")} ${item.idx + 1}`}
                </Typography>
                <Box sx={{ ml: 2, mr: 3, minWidth: 70 }}>
                  <ExpensesInput
                    value={decompValue}
                    onChange={handleDecompChange}
                    size="small"
                  />
                </Box>
              </Box>
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

ExpensesPartidaItem.propTypes = {
  partida: PropTypes.object.isRequired,
  selectedItems: PropTypes.object.isRequired,
  allocation: PropTypes.object.isRequired,
  setAllocation: PropTypes.func.isRequired,
  onChildEdit: PropTypes.func,
  expandedAccordions: PropTypes.object,
  setExpandedAccordions: PropTypes.func,
}

ExpensesPartidaItem.defaultProps = {
  onChildEdit: () => {},
}

export default ExpensesPartidaItem
